/**
 * Number localization utilities for speech synthesis
 */

export type SupportedLanguage = 'en' | 'zh';

/**
 * Convert number to localized spoken format
 * For English: keeps Arabic numerals (1, 2, 3...)
 * For Chinese: converts to Chinese numerals where appropriate for speech
 */
export function localizeNumber(num: number, language: SupportedLanguage): string {
  if (language === 'zh') {
    return convertToChineseNumber(num);
  }
  return num.toString();
}

/**
 * Convert Arabic numerals to Chinese numerals for better speech synthesis
 */
function convertToChineseNumber(num: number): string {
  if (num === 0) return '零';
  if (num < 0) return '负' + convertToChineseNumber(-num);

  const digits = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  const units = ['', '十', '百', '千'];
  const bigUnits = ['', '万', '亿'];

  if (num < 10) {
    return digits[num];
  }

  if (num < 100) {
    const tens = Math.floor(num / 10);
    const ones = num % 10;
    
    if (tens === 1) {
      return ones === 0 ? '十' : '十' + digits[ones];
    } else {
      return digits[tens] + '十' + (ones === 0 ? '' : digits[ones]);
    }
  }

  // For larger numbers, use a more comprehensive conversion
  // For simplicity, we'll handle up to 999 for common timer use cases
  if (num < 1000) {
    const hundreds = Math.floor(num / 100);
    const remainder = num % 100;
    
    let result = digits[hundreds] + '百';
    
    if (remainder === 0) {
      return result;
    } else if (remainder < 10) {
      return result + '零' + digits[remainder];
    } else {
      const tens = Math.floor(remainder / 10);
      const ones = remainder % 10;
      
      if (tens === 1) {
        result += '一十';
      } else {
        result += digits[tens] + '十';
      }
      
      if (ones > 0) {
        result += digits[ones];
      }
      
      return result;
    }
  }

  // For very large numbers (unlikely for timer), fall back to Arabic numerals
  return num.toString();
}

/**
 * Format time values for speech synthesis
 */
export function localizeTimeForSpeech(
  minutes: number, 
  seconds: number, 
  language: SupportedLanguage
): { minutes: string; seconds: string } {
  return {
    minutes: localizeNumber(minutes, language),
    seconds: localizeNumber(seconds, language)
  };
}